import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Top-level React error boundary that catches rendering errors and displays
 * a recoverable error UI instead of a blank white screen.
 *
 * When Sentry DSN is configured, errors are reported automatically.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught error:', error, info.componentStack)

    // Report to Sentry if the global Sentry SDK is loaded
    const w = window as unknown as Record<string, unknown>
    if (
      w.__SENTRY__ &&
      typeof (w.__SENTRY__ as Record<string, unknown>).captureException === 'function'
    ) {
      ;(w.__SENTRY__ as { captureException: (e: Error, ctx?: unknown) => void }).captureException(
        error,
        {
          contexts: { react: { componentStack: info.componentStack } },
        }
      )
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-transparent px-4">
          <div className="max-w-lg w-full bg-harbor-surface border border-harbor-border rounded-xl p-8 text-center">
            <div className="text-5xl mb-4">💥</div>
            <h1 className="text-2xl font-bold text-harbor-text mb-2">Something went wrong</h1>
            <p className="text-harbor-text-secondary mb-6">
              An unexpected error occurred while rendering this page. You can try reloading or
              returning to the dashboard.
            </p>
            {this.state.error && (
              <pre className="text-left text-xs bg-harbor-surface-raised border border-harbor-border rounded-lg p-4 mb-6 overflow-auto max-h-40 text-red-400 font-mono">
                {this.state.error.message}
              </pre>
            )}
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={this.handleReset}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
              <button
                type="button"
                onClick={() => window.location.assign('/dashboard')}
                className="px-4 py-2 bg-harbor-border hover:bg-harbor-surface-raised text-harbor-text rounded-lg font-medium transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
